// ** Next Import
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";

// ** MUI Imports
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled, useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "src/assets/logo.png";

const StyledLink = styled(Link)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  textDecoration: "none",
  marginRight: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    marginRight: theme.spacing(8),
  },
}));

const NavLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  marginLeft: theme.spacing(3),
  textDecoration: "none",
  color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: isActive ? 600 : 500,
  borderBottom: isActive ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
  paddingBottom: theme.spacing(0.5),
  transition: "all 0.3s ease",
  "&:hover": {
    //textDecoration: "underline",
    color: theme.palette.primary.main,
  },
}));

const MobileNavLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  textDecoration: "none",
  color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: isActive ? 600 : 500,
  padding: theme.spacing(1, 2),
  display: "block",
  backgroundColor: isActive ? theme.palette.primary.light : "transparent",
  borderRadius: theme.spacing(0.5),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: isActive ? theme.palette.primary.light : theme.palette.action.hover,
  },
}));

const BlankLayoutAppBar = () => {
  // ** Hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const isMobileMenuOpen = Boolean(mobileMenuAnchor);
  const location = useLocation();

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  // Function to check if a path is active
  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const navigationItems = [
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms and Conditions", path: "/terms-and-conditions" },
    { label: "About", path: "/about" },
  ];

  return (
    <AppBar elevation={3} color="default" position="sticky">
      <Toolbar
        sx={{
          justifyContent: "space-between",
          p: (theme) => `${theme.spacing(0, 2)} !important`,
          [theme.breakpoints.up('md')]: {
            p: (theme) => `${theme.spacing(0, 6)} !important`,
          },
          minHeight: `${theme.mixins.toolbar.minHeight}px !important`,
        }}
      >
        {/* Logo */}
        <StyledLink to="/">
          <img 
            src={Logo} 
            alt="Logo" 
            style={{ 
              height: isMobile ? "60px" : "100px", 
              width: isMobile ? "108px" : "180px",
              objectFit: "contain"
            }} 
          />
        </StyledLink>

        {/* Title - Hidden on mobile, shown on tablet and up */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            lineHeight: "normal",
            textTransform: "uppercase",
            display: { xs: "none", md: "block" },
            textAlign: "center",
            flex: 1,
            mx: 2,
          }}
        >
          Ahmedabad Janmarg Limited (AMTS | BRTS)
        </Typography>

        {/* Desktop Navigation */}
        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}>
          {navigationItems.map((item) => (
            <NavLink 
              key={item.path} 
              to={item.path}
              isActive={isActivePath(item.path)}
            >
              {item.label}
            </NavLink>
          ))}
        </Box>

        {/* Mobile Menu Button */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleMobileMenuOpen}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Toolbar>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: 200,
            mt: 1,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
            Ahmedabad Janmarg Limited (BRTS)
          </Typography>
        </Box>
        {navigationItems.map((item) => (
          <MenuItem key={item.path} onClick={handleMobileMenuClose}>
            <MobileNavLink 
              to={item.path}
              isActive={isActivePath(item.path)}
            >
              {item.label}
            </MobileNavLink>
          </MenuItem>
        ))}
      </Menu>
    </AppBar>
  );
};

export default BlankLayoutAppBar;
